<template>
  <div class="bonus-rate-container bg-[#104939] p-[12px] w-full">
    <!-- 顶部选项卡 -->
    <div class="tabs" ref="tabsRef">
      <div v-for="(type, idx) in gameTypes" :key="type" :class="['tab', { active: idx === activeTab }]"
        @click="handleTabClick(idx)" ref="tabRefs">
        {{ type }}
      </div>
      <div class="tab-underline" :style="{
        width: underlineStyle.width,
        left: underlineStyle.left,
      }"></div>
    </div>
    <!-- 表格 -->
    <div class="table-wrap w-full">
      <div class="table-header w-full">
        <div class="th w-1/6 overflow-hidden">
          {{ $t('convidarCentro.agentBonusRate.ptmLevel') }}
        </div>
        <div class="th w-1/6 overflow-hidden">{{ $t('convidarCentro.agentBonusRate.level1') }}</div>
        <div class="th w-1/6 overflow-hidden">{{ $t('convidarCentro.agentBonusRate.level2') }}</div>
        <div class="th w-1/6 overflow-hidden">{{ $t('convidarCentro.agentBonusRate.level3') }}</div>
        <div class="th w-1/6 overflow-hidden">{{ $t('convidarCentro.agentBonusRate.level4') }}</div>
        <div class="th w-1/6 overflow-hidden">{{ $t('convidarCentro.agentBonusRate.level5') }}</div>
      </div>
      <div v-for="(row, idx) in tableData[activeTab]" :key="row.tier" :class="['table-row', { zebra: idx % 2 === 1 }]">
        <div class="td w-1/6">{{ row.tier }}</div>
        <div class="td w-1/6">
          <img v-if="row.lv1 === 'lock'" :src="getAssetsFile('activity/tucengse.png')" class="lock-icon" alt="lock" />
          <span v-else class="highlight">{{ row.lv1 }}</span>
        </div>
        <div class="td w-1/6">
          <img v-if="row.lv2 === 'lock'" :src="getAssetsFile('activity/tucengse.png')" class="lock-icon" alt="lock" />
          <span v-else class="highlight">{{ row.lv2 }}</span>
        </div>
        <div class="td w-1/6">
          <img v-if="row.lv3 === 'lock'" :src="getAssetsFile('activity/tucengse.png')" class="lock-icon" alt="lock" />
          <span v-else class="highlight">{{ row.lv3 }}</span>
        </div>
        <div class="td w-1/6">
          <img v-if="row.lv4 === 'lock'" :src="getAssetsFile('activity/tucengse.png')" class="lock-icon" alt="lock" />
          <span v-else class="highlight">{{ row.lv4 }}</span>
        </div>
        <div class="td w-1/6">
          <img v-if="row.lv5 === 'lock'" :src="getAssetsFile('activity/tucengse.png')" class="lock-icon" alt="lock" />
          <span v-else class="highlight">{{ row.lv5 }}</span>
        </div>
      </div>
    </div>
    <div class="text-[10px] color-[#FFFFFF]  mt-[10px] mx-[11px]" style="line-height: 30px;">
      {{ $t('convidarCentro.agentBonusRate.commissionNote') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, watch } from 'vue'
import { getPromoterConfig } from '@/api/game' // 请替换为实际API路径
import { getAssetsFile } from '@/utils/pub-use' // 保证你有这个方法
import { useI18n } from 'vue-i18n'
import { numberFormat } from '@/utils/formatter'
const { t } = useI18n()

const activeTab = ref(0)
const tabRefs = ref([])
const tabsRef = ref(null)
const underlineStyle = ref({ width: '0px', left: '0px' })

// 动态数据
const gameTypes = ref<string[]>([])
const promoterLevels = ref<any[]>([])
const tableData = ref<any[][]>([])

// 游戏类型映射（使用翻译）
function getGameTypeName(type: string) {
  // 直接返回原始的游戏类型名称
  return type
}

// 获取数据并处理
async function fetchConfig() {
  try {
    const res = await getPromoterConfig()

    // 检查响应结构，确保有正确的数据
    if (res && res.data && res.data.commissionLevelInfo) {
      // 2. 取出所有等级
      promoterLevels.value = res.data.promoterLevelInfo || []

      // 使用所有返佣等级信息，不区分类型
      const allLevels = res.data.commissionLevelInfo || []

      // 1. 取出所有有用的gameType（不创建"全部"标签）
      const allTypes = Array.from(new Set(
        allLevels.map((item: any) => item.gameType).filter(Boolean)
      )).filter((type): type is string => typeof type === 'string')
      
      gameTypes.value = allTypes

      // 3. 构造表格数据
      if (allTypes.length > 0) {
        // 有类型时：按类型过滤数据，为每个类型生成独立的表格
        tableData.value = allTypes.map(type => {
          // 取出该类型下所有等级的返佣比例
          const typeLevels = allLevels.filter((item: any) => item.gameType === type)

          // 以tier为行，level为列
          const maxTier = 5
          const rows = []

          for (let tier = 1; tier <= maxTier; tier++) {
            const row: any = { tier: `${t('convidarCentro.agentBonusRate.tier')}${tier}` }

            // 遍历所有level (1-5)
            for (let level = 1; level <= 5; level++) {
              if (level < tier) {
                // 如果 level 小于当前 tier，则显示锁定
                row[`lv${level}`] = 'lock'
              } else {
                // 如果 level >= tier，则显示该 tier 对应的比例
                const targetLevel = tier
                const found = typeLevels.find((l: any) => l.level === targetLevel)
                if (found && found.ratio !== undefined) {
                  const percentage = numberFormat(Number(found.ratio) * 100, { minDecimals: 2 })
                  row[`lv${level}`] = `${percentage}%`
                } else {
                  row[`lv${level}`] = '0.00%'
                }
              }
            }
            rows.push(row)
          }
          return rows
        })
      } else {
        // 没有类型时：使用所有数据，不区分类型，只生成一个表格
        const maxTier = 5
        const rows = []

        for (let tier = 1; tier <= maxTier; tier++) {
          const row: any = { tier: `${t('convidarCentro.agentBonusRate.tier')}${tier}` }

          // 遍历所有level (1-5)
          for (let level = 1; level <= 5; level++) {
            if (level < tier) {
              row[`lv${level}`] = 'lock'
            } else {
              const targetLevel = tier
              // 从所有数据中查找，不区分类型
              const found = allLevels.find((l: any) => l.level === targetLevel)
              if (found && found.ratio !== undefined) {
                const percentage = numberFormat(Number(found.ratio) * 100, { minDecimals: 2 })
                row[`lv${level}`] = `${percentage}%`
              } else {
                row[`lv${level}`] = '0.00%'
              }
            }
          }
          rows.push(row)
        }
        tableData.value = [rows]
      }
    } else {
      // 即使没有数据，也生成一个默认表格（不显示标签）
      gameTypes.value = []
      promoterLevels.value = []
      
      // 生成默认表格数据
      const maxTier = 5
      const rows = []
      for (let tier = 1; tier <= maxTier; tier++) {
        const row: any = { tier: `${t('convidarCentro.agentBonusRate.tier')}${tier}` }
        for (let level = 1; level <= 5; level++) {
          if (level < tier) {
            row[`lv${level}`] = 'lock'
          } else {
            row[`lv${level}`] = '0.00%'
          }
        }
        rows.push(row)
      }
      tableData.value = [rows]
    }
  } catch (error) {
    console.error('获取配置失败:', error)
    // 即使出错，也生成一个默认表格（不显示标签）
    gameTypes.value = []
    promoterLevels.value = []
    
    // 生成默认表格数据
    const maxTier = 5
    const rows = []
    for (let tier = 1; tier <= maxTier; tier++) {
      const row: any = { tier: `${t('convidarCentro.agentBonusRate.tier')}${tier}` }
      for (let level = 1; level <= 5; level++) {
        if (level < tier) {
          row[`lv${level}`] = 'lock'
        } else {
          row[`lv${level}`] = '0.00%'
        }
      }
      rows.push(row)
    }
    tableData.value = [rows]
  }
}

// 点击标签时的滚动效果
function handleTabClick(idx: number) {
  activeTab.value = idx
  scrollToTab(idx)
}

// 滚动到指定标签
function scrollToTab(idx: number) {
  nextTick(() => {
    const tabsContainer = tabsRef.value
    const targetTab = tabRefs.value[idx]

    if (tabsContainer && targetTab) {
      const containerWidth = tabsContainer.offsetWidth
      const tabLeft = targetTab.offsetLeft
      const tabWidth = targetTab.offsetWidth
      const scrollLeft = tabsContainer.scrollLeft

      // 计算目标滚动位置
      const targetScrollLeft = tabLeft - (containerWidth / 2) + (tabWidth / 2)

      // 平滑滚动到目标位置
      tabsContainer.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth'
      })
    }
  })
}

function updateUnderline() {
  nextTick(() => {
    // @ts-ignore
    const tabEl = tabRefs.value[activeTab.value]
    if (tabEl) {
      underlineStyle.value = {
        width: tabEl.offsetWidth + 'px',
        left: tabEl.offsetLeft + 'px'
      }
    }
  })
}

onMounted(async () => {
  await fetchConfig()
  updateUnderline()
})
watch(activeTab, updateUnderline)
</script>

<style scoped>
.bonus-rate-container {

  min-height: 100vh;
}

.tabs {
  display: flex;

  position: relative;
  overflow-x: auto;
}

.tab {
  flex: 1 0 auto;
  text-align: center;
  padding: 18px 0 10px 0;
  /* 从8px改为10px，增加底部内边距让横线和文字有合适间距 */
  color: #5FB589;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
  position: relative;
  transition: color 0.2s, background 0.15s;
  z-index: 1;
  min-width: 80px;
}

.tab.active {
  color: #fff;
  font-weight: bold;
}

.tab:active {
  background: #00593D;
  color: #5FB589 !important;
}

.tab-underline {
  position: absolute;
  bottom: 0;
  /* 改回0 */
  height: 2px;
  /* 保持2px高度 */
  background: #009F76;
  border-radius: 2px 2px 0 0;
  transition: left 0.3s, width 0.3s;
  z-index: 2;
}

.table-wrap {
  /* margin: 4px 0 0 0; */
  /* 从6px改为4px，进一步减少顶部外边距 */
  /* overflow-x: auto; */
}

.table-scroll {
  min-width: 420px;
  /* 从480px改为420px */
}

.table-header {
  display: flex;
  background: #176343;
  color: #fff;
  font-size: 11px;
  padding: 12px 0 8px 0;
  /* 从15px 0 10px 0改为12px 0 8px 0 */
}

.th {
  flex: 1;
  text-align: center;

}

.table-row {
  display: flex;
  background: #18553a;
  color: #b6e2d3;
  font-size: 11px;
  padding: 12px 0;
  /* 从16px 0改为12px 0 */
  align-items: center;
  transition: background 0.2s;
}

.table-row.zebra {
  background: #176343;
}

.td {
  flex: 1;
  text-align: center;
  /* min-width: 60px; */
  /* 从70px改为60px */
}

.highlight {
  color: #ffe25a;
  font-weight: bold;
}

.lock-icon {
  width: 14px;
  /* 从16px改为14px */
  height: 14px;
  /* 从16px改为14px */
  display: inline-block;
  vertical-align: middle;
}
</style>
